import React from 'react';
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
  InformationCircleIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';

type BannerVariant = 'success' | 'warning' | 'error' | 'info' | 'welcome';

interface StatusBannerProps {
  variant: BannerVariant;
  title: string;
  message?: string;
  icon?: React.ReactNode;
  actions?: React.ReactNode;
  dismissible?: boolean;
  onDismiss?: () => void;
  className?: string;
}

/**
 * StatusBanner Component - Clinic Studios Design System
 *
 * Variants:
 * - success: Sage green - confirmations, completed actions
 * - warning: Gold - attention needed, pending states
 * - error: Terracotta - errors, failures
 * - info: Primary light - neutral information
 * - welcome: Special warm variant for onboarding
 */
const StatusBanner: React.FC<StatusBannerProps> = ({
  variant,
  title,
  message,
  icon,
  actions,
  dismissible = false,
  onDismiss,
  className = '',
}) => {
  const variants: Record<BannerVariant, {
    bg: string;
    border: string;
    iconColor: string;
    titleColor: string;
    textColor: string;
    defaultIcon: React.ReactNode;
  }> = {
    success: {
      bg: 'bg-clinic-primary/10',
      border: 'border-l-4 border-clinic-primary',
      iconColor: 'text-clinic-primary',
      titleColor: 'text-clinic-dark',
      textColor: 'text-clinic-dark/70',
      defaultIcon: <CheckCircleIcon className="w-6 h-6" />,
    },
    warning: {
      bg: 'bg-clinic-gold/10',
      border: 'border-l-4 border-clinic-gold',
      iconColor: 'text-clinic-gold',
      titleColor: 'text-clinic-dark',
      textColor: 'text-clinic-dark/70',
      defaultIcon: <ExclamationTriangleIcon className="w-6 h-6" />,
    },
    error: {
      bg: 'bg-clinic-accent/10',
      border: 'border-l-4 border-clinic-accent',
      iconColor: 'text-clinic-accent',
      titleColor: 'text-clinic-dark',
      textColor: 'text-clinic-dark/70',
      defaultIcon: <XCircleIcon className="w-6 h-6" />,
    },
    info: {
      bg: 'bg-clinic-primary-lighter',
      border: 'border-l-4 border-clinic-primary-light',
      iconColor: 'text-clinic-primary',
      titleColor: 'text-clinic-dark',
      textColor: 'text-clinic-dark/70',
      defaultIcon: <InformationCircleIcon className="w-6 h-6" />,
    },
    welcome: {
      bg: 'bg-gradient-to-br from-clinic-cream to-clinic-primary/5',
      border: 'border border-clinic-primary-light rounded-xl',
      iconColor: 'text-clinic-primary',
      titleColor: 'text-clinic-dark',
      textColor: 'text-clinic-dark/70',
      defaultIcon: <SparklesIcon className="w-6 h-6" />,
    },
  };

  const config = variants[variant];
  const displayIcon = icon || config.defaultIcon;

  return (
    <div
      className={`
        ${config.bg}
        ${config.border}
        ${variant === 'welcome' ? 'p-6' : 'p-4'}
        ${className}
      `.replace(/\s+/g, ' ').trim()}
      role="alert"
    >
      <div className="flex">
        <div className={`flex-shrink-0 ${config.iconColor}`}>
          {displayIcon}
        </div>
        <div className="ml-4 flex-1">
          <h3 className={`text-base font-semibold ${config.titleColor} font-body`}>
            {title}
          </h3>
          {message && (
            <p className={`mt-1 text-sm ${config.textColor}`}>
              {message}
            </p>
          )}
          {actions && (
            <div className="mt-4 flex flex-wrap gap-3">
              {actions}
            </div>
          )}
        </div>
        {dismissible && onDismiss && (
          <div className="flex-shrink-0 ml-4">
            <button
              type="button"
              onClick={onDismiss}
              className="inline-flex rounded-md p-1.5 text-clinic-muted hover:text-clinic-dark hover:bg-black/5 focus:outline-none focus:ring-2 focus:ring-clinic-primary transition-colors"
            >
              <span className="sr-only">Dismiss</span>
              <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path
                  fillRule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * WelcomeBanner - Specialized onboarding component
 *
 * Used for new patient welcome experience (fixes U7)
 */
interface WelcomeBannerProps {
  userName?: string;
  primaryAction?: React.ReactNode;
  secondaryAction?: React.ReactNode;
  className?: string;
}

export const WelcomeBanner: React.FC<WelcomeBannerProps> = ({
  userName,
  primaryAction,
  secondaryAction,
  className = '',
}) => {
  return (
    <div
      className={`
        relative overflow-hidden
        bg-gradient-to-br from-clinic-cream via-white to-clinic-primary/5
        border border-clinic-primary-light
        rounded-2xl p-8
        ${className}
      `}
    >
      {/* Decorative elements */}
      <div className="absolute top-0 right-0 w-64 h-64 bg-clinic-primary/5 rounded-full -translate-y-1/2 translate-x-1/2" />
      <div className="absolute bottom-0 left-0 w-32 h-32 bg-clinic-gold/10 rounded-full translate-y-1/2 -translate-x-1/2" />

      <div className="relative">
        {/* Welcome icon */}
        <div className="w-14 h-14 bg-clinic-primary/10 rounded-2xl flex items-center justify-center mb-5">
          <SparklesIcon className="w-7 h-7 text-clinic-primary" />
        </div>

        {/* Welcome text */}
        <h2 className="text-2xl font-bold text-clinic-dark font-body mb-2">
          {userName ? `Welcome, ${userName}!` : 'Welcome to Clinic Studios!'}
        </h2>
        <p className="text-clinic-dark/70 text-base mb-6 max-w-lg">
          Get started by browsing our clinician directory to find a provider, or enter an invite code if one was shared with you.
        </p>

        {/* Actions */}
        {(primaryAction || secondaryAction) && (
          <div className="flex flex-wrap gap-3">
            {primaryAction}
            {secondaryAction}
          </div>
        )}
      </div>
    </div>
  );
};

export default StatusBanner;
